import Popup from "../../lib/Popup";

import * as PIXI from "pixi.js";
import gsap from "gsap";

import Button from "../../lib/Button";
import Utils from "../../Utils";
import Player from "../game/Player";
import { game_event_type } from "../../service/MultiplayerEventManager";
import { IGameSnacks } from "../../lib/GameSnacks";
import ServiceLocator from "../../ServiceLocator";
import { LiveEventData } from "../../lib/game_snacks/ILiveMultiplayer";
import Session, { game_type, IAsyncGameState } from "../../Session";
import { Profile } from "../../lib/game_snacks/IUser";
import SpriteMaskAnim from "../../lib/SpriteMaskAnim";
import { GameSession } from "../../lib/game_snacks/IMultiplayer";

declare global {
    interface Window { GameSnacks: IGameSnacks; }
}


export default abstract class GameOverPopup extends Popup
{
    // #region Attributes //

    // protected:

    protected _arrPlayer: Array<Player> = null;
    protected _isRematchRequestReceived: boolean = null;
    protected _isFriendInLobbyReceived: boolean = null;

    protected _myPlayerId: number = null;
    protected _oppPlayerId: number = null;

    protected _bannerWrapper: PIXI.Container = null;
    protected _bannerMaskIn: SpriteMaskAnim = null;
    protected _bannerMessage: PIXI.Sprite = null;
    protected _rematchMessage: PIXI.Sprite = null;
    protected _rematchBubble: PIXI.Sprite = null;
    protected _inLobbyBubble: PIXI.Sprite = null;
    protected _homeBtn: Button = null;
    protected _lobbyBtn: Button = null;
    protected _retryBtn: Button = null;
    protected _footerTitle: PIXI.Text = null;

    protected _backLayer: PIXI.Container = null;
    protected _introLayer: PIXI.Container = null;
    protected _playerSlotLayer: PIXI.Container = null;
    protected _introEndLayer: PIXI.Container = null;

    private _showRematchBannerTl: gsap.core.Timeline = null;

    private _checkAsyncStateTimer: number = null;

    protected readonly _kArrPlayerSlotPos: Array<PIXI.Point> = [
        new PIXI.Point( 158, 514 - 60 ), // V2:
        new PIXI.Point( ServiceLocator.app.width - 158, 514 - 60 ) ]; // V2:

    // #endregion //


    public set players( value: Array<Player> ) { this._arrPlayer = value; }
    public set isRematchRequestReceived( value: boolean ) { this._isRematchRequestReceived = value; }
    public set isFriendInLobbyReceived( value: boolean ) { this._isFriendInLobbyReceived = value; }


    // #region Methods //

    // public:

    public constructor() 
    {
        super();
    }

    public onEnter( params: any = null ): void
    {
        super.onEnter( params );

        console.assert( this._arrPlayer != null, "GameOverPopup.ts :: onEnter() :: this._arrPlayer cannot be null." );
        console.assert( this._isRematchRequestReceived !== null, "GameOverPopup.ts :: onEnter() :: this._isRematchRequestReceived cannot be null." );
        console.assert( this._isFriendInLobbyReceived !== null, "GameOverPopup.ts :: onEnter() :: this._isFriendInLobbyReceived cannot be null." );

        this._myPlayerId = Session.i.profile.uid == Session.i.room[ 0 ].uid ? 0 : 1;
        this._oppPlayerId = this._myPlayerId == 0 ? 1 : 0;
        this._checkAsyncStateTimer = 0;

        this._rematchBubble = new PIXI.Sprite( ServiceLocator.resources.rematch_bubble.texture );
        this._rematchBubble.anchor.set( 201/208, 1 );
        this._rematchBubble.visible = false;

        this._inLobbyBubble = new PIXI.Sprite( ServiceLocator.resources.lobby_bubble.texture );
        this._inLobbyBubble.anchor.set( 201/208, 1 );
        this._inLobbyBubble.visible = false;

        this._rematchMessage = new PIXI.Sprite( ServiceLocator.resources.rematch.texture );
        this._rematchMessage.anchor.set( 0.5 );
        this._rematchMessage.visible = false;

        ServiceLocator.multiplayerEventManager.onProfileChange.add( this._onProfile_Changed, this );
    }

        protected _onProfile_Changed( profile: Profile ): void
        {
            ( Session.i.profile.username as any ) = profile.username;
            ( Session.i.profile.avatar as any ) = profile.avatar;
            Session.i.profile.isFake = false;
        }

    public onLeave(): void
    {
        ServiceLocator.multiplayerEventManager.onProfileChange.remove( this._onProfile_Changed, this );

        if ( this._showRematchBannerTl )
        {
            this._showRematchBannerTl.kill();
            this._showRematchBanner = null;
        }

        super.onLeave();
    }

    public onUpdate( dt: number ): void
    {
        if ( Session.i.isMessages )
        {
            this._checkAsyncStateTimer += dt;
            if ( this._checkAsyncStateTimer >= 0 )
            {
                this._checkAsyncStateTimer += dt;
                if ( this._checkAsyncStateTimer >= 1 )
                {
                    this._checkAsyncStateTimer = -1;

                    window.GameSnacks.multiplayer.getGameState().then( ( gameSession: GameSession ) => {
                        this._checkAsyncStateTimer = 0; 

                        let gameState: IAsyncGameState = JSON.parse( gameSession.gameState );
                        if ( !this._isRematchRequestReceived && gameState.arrIsRematchRequested[ this._oppPlayerId ] )
                        {
                            this.onRematchAsked();
                        }
                        else if ( gameState.arrIsRematchRequested[ 0 ] && gameState.arrIsRematchRequested[ 1 ] )
                        {
                            this.onRematchAccepted();
                        }
                    } );
                }
            }
        }
    }

    protected _onHomeBtn_Click(): void
    {
        this._onClosed.dispatch( this);
    }

    protected _onRetryBtn_Click(): void
    {
        this._onClosed.dispatch( this, { isPlayAgain: true } );
    }

    protected _onAskRematchBtn_Click(): void
    {
        if ( this._isRematchRequestReceived )
        {
            window.GameSnacks.liveMultiplayer.sendEvent( { payload: JSON.stringify( { type: game_event_type.REMATCH_ACCEPTED } ) } );

            this._onClosed.dispatch( this, { isPlayAgain: true, isRematch: true } );
        }
        else
        {
            this._retryBtn.setEnabled( false );

            this._rematchBubble.position.copyFrom( this._kArrPlayerSlotPos[ this._myPlayerId ] );
            this._rematchBubble.x += 110;
            this._rematchBubble.y -= 110;

            this._showRematchBanner();
            this._showRematchBubble();

            this._footerTitle.text = "Rematch asked!\nWaiting for response...";
            this._footerTitle.style.align = "center";

            window.GameSnacks.liveMultiplayer.sendEvent( { payload: JSON.stringify( { type: game_event_type.REMATCH_ASKED } ) } );
        }
    }

    protected _onAskAsyncRematchBtn_Click(): void
    {
        if ( this._isRematchRequestReceived )
        {
            this._onClosed.dispatch( this, { isPlayAgain: true, isRematch: true } );
        }
        else
        {
            this._retryBtn.setEnabled( false );

            this._rematchBubble.position.copyFrom( this._kArrPlayerSlotPos[ this._myPlayerId ] );
            this._rematchBubble.x += 110;
            this._rematchBubble.y -= 110;

            this._showRematchBanner();
            this._showRematchBubble();

            this._footerTitle.text = "Rematch asked!\nWaiting for response...";
            this._footerTitle.style.align = "center";

            window.GameSnacks.multiplayer.getGameState().then( ( gameSession: GameSession ) => {
                Session.i.asyncGameState = JSON.parse( gameSession.gameState );
                Session.i.asyncGameState.arrIsRematchRequested[ this._myPlayerId ] = true;
                window.GameSnacks.multiplayer.updateGameState( JSON.stringify( Session.i.asyncGameState ) );
            } );
        }
    }

    protected _onLobbyBtn_Click(): void
    {
        if ( !this._isFriendInLobbyReceived )
        {
            window.GameSnacks.liveMultiplayer.sendEvent( { payload: JSON.stringify( { type: game_event_type.FRIEND_BACK_TO_LOBBY } ) } );
        }

        this._onClosed.dispatch( this, { isPlayAgain: true } );
    }

    protected _createNavigationButtons(): void
    {
        this._homeBtn = new Button( { on: "go_button_home", pressed: "go_button_home_pressed" } );
        this._homeBtn.anchor.set( 0.5 );
        this._homeBtn.position.set( 175, 1009 );
        this._introEndLayer.addChild( this._homeBtn );
        this._homeBtn.on( "pointertap", this._onHomeBtn_Click.bind( this ) );
        
        if ( Session.i.gameType == game_type.CLASSIC_PRIVATE 
            || Session.i.gameType == game_type.POINTS_PRIVATE )
        {
            this._initPrivateNavigationButtons();
        }
        else if ( Session.i.gameType == game_type.CLASSIC_ASYNC )
        {
            this._initAsyncNavigationButtons();
        }
        else
        {
            this._retryBtn = new Button( { on: "button_retry", pressed: "button_retry_pressed", off: "button_retry_pressed" } );
            this._retryBtn.anchor.set( 0.5 );
            this._retryBtn.position.set( 458, 1010 );
            this._introEndLayer.addChild( this._retryBtn );
            this._retryBtn.on( "pointertap", this._onRetryBtn_Click.bind( this ) );
        }

        if ( Session.i.gameType == game_type.CLASSIC_ASYNC )
        {
            this._homeBtn.visible = false;
            this._retryBtn.visible = false;
        }
    }

        private _initPrivateNavigationButtons(): void
        {
            this._homeBtn.visible = false;
            //
            this._lobbyBtn = new Button( { on: "button_lobby", pressed: "button_lobby_pressed" } );
            this._lobbyBtn.anchor.set( 0.5 );
            this._lobbyBtn.position.copyFrom( this._homeBtn.position );
            this._introEndLayer.addChild( this._lobbyBtn );
            this._lobbyBtn.on( "pointertap", this._onLobbyBtn_Click.bind( this ) );

            this._introEndLayer.addChild( this._rematchBubble );
            this._introEndLayer.addChild( this._inLobbyBubble );

            if ( !this._isFriendInLobbyReceived )
            {
                if ( !this._isFriendInLobbyReceived && this._isRematchRequestReceived )
                {
                    this._rematchBubble.position.copyFrom( this._kArrPlayerSlotPos[ this._oppPlayerId ] );
                    this._rematchBubble.x += 110;
                    this._rematchBubble.y -= 110;

                    this._retryBtn = new Button( { on: "button_accept_rematch", pressed: "button_accept_rematch_pressed", off: "button_accept_rematch_pressed" } );
                
                    this.onRematchAsked();
                }
                else
                {
                    this._retryBtn = new Button( { on: "button_ask_rematch", pressed: "button_ask_rematch_pressed", off: "button_ask_rematch_pressed" } );
                }
                this._retryBtn.anchor.set( 0.5 );
                this._retryBtn.position.set( 458, 1010 );
                this._introEndLayer.addChild( this._retryBtn );
                this._retryBtn.once( "pointertap", this._onAskRematchBtn_Click.bind( this ) );
            }

            if ( this._isFriendInLobbyReceived )
            {
                this._lobbyBtn.x = ServiceLocator.app.width * 0.5;
            }
        }

        private _initAsyncNavigationButtons(): void
        {
            this._homeBtn.visible = false;

            this._introEndLayer.addChild( this._rematchBubble );

            if ( this._isRematchRequestReceived )
            {
                this._rematchBubble.position.copyFrom( this._kArrPlayerSlotPos[ this._oppPlayerId ] );
                this._rematchBubble.x += 110;
                this._rematchBubble.y -= 110;

                this._retryBtn = new Button( { on: "button_accept_rematch", pressed: "button_accept_rematch_pressed", off: "button_accept_rematch_pressed" } );
            
                this.onRematchAsked();
            }
            else
            {
                this._retryBtn = new Button( { on: "button_ask_rematch", pressed: "button_ask_rematch_pressed", off: "button_ask_rematch_pressed" } );
            }
            this._retryBtn.anchor.set( 0.5 );
            this._retryBtn.position.set( ServiceLocator.app.width * 0.5, 1010 );
            this._retryBtn.once( "pointertap", this._onAskAsyncRematchBtn_Click.bind( this ) );
            this._introEndLayer.addChild( this._retryBtn );
        }

    public onHostLeft(): void
    {
        if ( Session.i.gameType == game_type.CLASSIC_PRIVATE
            || Session.i.gameType == game_type.POINTS_PRIVATE )
        {
            this._rematchBubble.visible = false;

            if ( this._showRematchBannerTl )
            {
                this._showRematchBannerTl.progress( 1, false );
                this._showRematchBannerTl.kill();

                this._showRematchBannerTl.to( this._bannerWrapper.scale, { y: 0, duration: 0.24, ease: "back.in" } );
                this._showRematchBannerTl.call( () => {
                    this._rematchMessage.visible = false;
                    this._bannerMessage.visible = true;
                }, null, );
                this._showRematchBannerTl.to( this._bannerWrapper.scale, { y: 1, duration: 0.24, ease: "back.out" } );
                ServiceLocator.game.tweenGroup.add( this._showRematchBannerTl );
            }

            this._retryBtn.visible = false;
            this._homeBtn.visible = true;
            this._homeBtn.x = ServiceLocator.app.width * 0.5;

            this._inLobbyBubble.visible = false;
            this._lobbyBtn.visible = false;

            this._footerTitle.text = "One more game?";
        }
    }

    public onGuestLeft(): void
    {
        if ( Session.i.gameType == game_type.CLASSIC_PRIVATE
            || Session.i.gameType == game_type.POINTS_PRIVATE )
        {
            this._rematchBubble.visible = false;

            if ( this._showRematchBannerTl )
            {
                this._showRematchBannerTl.progress( 1, false );
                this._showRematchBannerTl.kill();

                this._showRematchBannerTl.to( this._bannerWrapper.scale, { y: 0, duration: 0.24, ease: "back.in" } );
                this._showRematchBannerTl.call( () => {
                    this._rematchMessage.visible = false;
                    this._bannerMessage.visible = true;
                }, null, );
                this._showRematchBannerTl.to( this._bannerWrapper.scale, { y: 1, duration: 0.24, ease: "back.out" } );
                ServiceLocator.game.tweenGroup.add( this._showRematchBannerTl );
            }

            this._retryBtn.visible = false;
            this._lobbyBtn.x = ServiceLocator.app.width * 0.5;

            this._inLobbyBubble.visible = false;

            this._footerTitle.text = "One more game?";
        }
    }

    public onRematchAsked(): void
    {
        this._isRematchRequestReceived = true;

        this._rematchBubble.position.copyFrom( this._kArrPlayerSlotPos[ 1 ] );
        this._rematchBubble.x += 110;
        this._rematchBubble.y -= 110;

        this._showRematchBanner();
        this._showRematchBubble();

        this._footerTitle.text = Session.i.room[ this._oppPlayerId ].username.prefix + " wants to play again!";
    
        this._retryBtn.setTextures( { on: "button_accept_rematch", pressed: "button_accept_rematch_pressed", off: "button_accept_rematch_pressed" } );
        this._retryBtn.reset();
    }

        private _showRematchBanner(): void
        {
            this._bannerWrapper.addChild( this._rematchMessage );
            this._showRematchBannerTl = new gsap.core.Timeline();
            this._showRematchBannerTl.to( this._bannerWrapper.scale, { y: 0, duration: 0.24, ease: "back.in" } );
            this._showRematchBannerTl.call( () => {
                this._bannerMessage.visible = false;
                this._rematchMessage.visible = true;
            }, null, );
            this._showRematchBannerTl.to( this._bannerWrapper.scale, { y: 1, duration: 0.24, ease: "back.out" } );
            ServiceLocator.game.tweenGroup.add( this._showRematchBannerTl );
        }

        private _showRematchBubble(): void
        {
            this._rematchBubble.visible = true;
            this._rematchBubble.scale.set( 0 );
            //
            ServiceLocator.game.tweenGroup.add( 
                gsap.to( this._rematchBubble.scale, { x: 1, y: 1, duration: 0.4, ease: "back.out" } ) );
        }

    public onRematchAccepted(): void
    {
        this._onClosed.dispatch( this, { isPlayAgain: true, isRematch: true } );
    }

    public onFriendInLobby(): void
    {
        this._isFriendInLobbyReceived = true;

        if ( this._rematchMessage.visible )
        {
            let tl: gsap.core.Timeline = new gsap.core.Timeline();
            tl.to( this._bannerWrapper.scale, { y: 0, duration: 0.24, ease: "back.in" } );
            tl.call( () => {
                this._rematchMessage.visible = false;
                this._bannerMessage.visible = true;
            }, null, );
            tl.to( this._bannerWrapper.scale, { y: 1, duration: 0.24, ease: "back.out" } );
            ServiceLocator.game.tweenGroup.add( tl );
        }

        this._rematchBubble.visible = false;
        //
        this._inLobbyBubble.position.copyFrom( this._kArrPlayerSlotPos[ 1 ] );
        this._inLobbyBubble.x += 110;
        this._inLobbyBubble.y -= 110;
        this._showInLobbyBubble();

        this._footerTitle.text = Session.i.room[ this._oppPlayerId ].username.prefix + " is waiting\nin the game lobby...";
        this._footerTitle.style.align = "center";
    
        this._retryBtn.setEnabled( false );
    }

        private _showInLobbyBubble(): void
        {
            this._inLobbyBubble.visible = true;
            this._inLobbyBubble.scale.set( 0 );
            //
            ServiceLocator.game.tweenGroup.add( 
                gsap.to( this._inLobbyBubble.scale, { x: 1, y: 1, duration: 0.4, ease: "back.out" } ) );
        }

    // #endregion //
}

export interface IGameOverParams
{
    isPlayAgain?: boolean;
    isRematch?: boolean;
}